// src/Components/Headers.js
import { useEffect, useState, useRef, useCallback } from 'react';
import './header.css';
import { NavLink } from 'react-router-dom';
import axios from 'axios';

// Create a single axios instance with safe defaults for this app
const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, ''); // remove trailing slashes
const api = axios.create({
  baseURL: API_BASE || undefined,
  withCredentials: true,
  timeout: 8000, // reasonable request timeout
  headers: {
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Interceptor: centralized error handling for auth
api.interceptors.response.use(
  response => response,
  error => {
    if (
      error?.response?.status === 401 &&
      window.location.pathname !== '/login'
    ) {
      window.location.replace('/login');
    }
    return Promise.reject(error);
  }
);

const isLikelySafeImageSrc = (src) => {
  if (!src || typeof src !== 'string') return false;
  const lower = src.trim().toLowerCase();
  // eslint-disable-next-line no-script-url
  const JS_SCHEME = 'javascript:'; // prevent no-script-url warning

  if (lower.startsWith(JS_SCHEME)) return false;
  if (lower.startsWith('data:image/')) return true;
  if (lower.startsWith('http://') || lower.startsWith('https://')) return true;
  if (lower.startsWith('/') || lower.startsWith('./') || lower.startsWith('../')) return true;
  return false;
};

const Headers = () => {
  const [userdata, setUserdata] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [profileImage, setProfileImage] = useState('profile.png');
  const [showProfileText, setShowProfileText] = useState(false);
  const dropdownRef = useRef(null);

  const getUser = useCallback(async (signal) => {
    try {
      const resp = await api.get('/ttc/login/userdata', { signal });
      const user = resp?.data?.user ?? null;
      if (user) {
        setUserdata(user);
        const pic = user.picture || 'profile.png';
        setProfileImage(isLikelySafeImageSrc(pic) ? pic : 'profile.png');
      } else {
        setUserdata(null);
      }
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error('Error fetching user data', err);
        setUserdata(null);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    getUser(controller.signal);
    return () => controller.abort();
  }, [getUser]);

  const logout = useCallback(async () => {
    try {
      await api.post('/ttc/logout');
    } catch (err) {
      console.error('Logout request failed', err);
    } finally {
      window.location.replace('/ttc/login');
    }
  }, []);

  const toggleDropdown = useCallback(() => {
    setDropdownOpen(prev => !prev);
  }, []);

  const handleClickOutside = useCallback((event) => {
    const container = dropdownRef.current;
    if (container && !container.contains(event.target)) {
      setDropdownOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, { capture: true });
    return () => {
      document.removeEventListener('click', handleClickOutside, { capture: true });
    };
  }, [handleClickOutside]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') setDropdownOpen(false);
    if ((e.key === 'Enter' || e.key === ' ') && e.target?.closest('.dropdown-container')) {
      e.preventDefault();
      toggleDropdown();
    }
  }, [toggleDropdown]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleImgError = useCallback(() => {
    if (profileImage !== 'profile.png') {
      setProfileImage('profile.png');
    } else {
      setShowProfileText(true);
    }
  }, [profileImage]);

  return (
    <header>
      <nav>
        <div className="left">
          <NavLink to="/dashboard" className="logo-link">
            <h1>TTChef</h1>
          </NavLink>
        </div>
        <div className="right">
          <ul>
            {userdata ? (
              <li className="dropdown-container" ref={dropdownRef}>
                {!showProfileText ? (
                  <img
                    src={profileImage}
                    alt="Profile"
                    style={{ width: '60px', height: '60px', borderRadius: '50%', cursor: 'pointer' }}
                    onClick={toggleDropdown}
                    onError={handleImgError}
                    referrerPolicy="no-referrer"
                    crossOrigin={isLikelySafeImageSrc(profileImage) && profileImage.startsWith('http') ? 'anonymous' : undefined}
                    tabIndex={0}
                    role="button"
                    aria-haspopup="true"
                    aria-expanded={dropdownOpen}
                  />
                ) : (
                  <span
                    style={{ cursor: 'pointer', fontSize: '1rem', fontWeight: 'bold' }}
                    onClick={toggleDropdown}
                    tabIndex={0}
                    role="button"
                    aria-haspopup="true"
                    aria-expanded={dropdownOpen}
                  >
                    Profile
                  </span>
                )}
                {dropdownOpen && (
                  <ul className="dropdown" aria-label="profile menu">
                    <li>
                      <NavLink to="/profile">Profile</NavLink>
                    </li>
                    <li>
                      <NavLink to="/about">About</NavLink>
                    </li>
                    <li>
                      <button
                        onClick={logout}
                        className="dropdown-logout-button"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        Logout
                      </button>
                    </li>
                  </ul>
                )}
              </li>
            ) : (
              <li>
                <NavLink to="/login">Login</NavLink>
              </li>
            )}
          </ul>
        </div>
      </nav>
    </header>
  );
};

Headers.propTypes = {};

export default Headers;
